// https://confettijs.org/

/**
 * Configuration class for confetti settings
 */
class ConfettiConfig {
	constructor() {
		this.onClick = false;
		this.gravity = 10;
		this.particleCount = 75;
		this.particleSize = 1;
		this.explosionPower = 25;
		this.destroyTarget = true;
		this.fade = false;
	}
}

/**
 * 2D Vector class for position and velocity calculations
 */
class Vector2 {
	constructor(x = 0, y = 0) {
		this.x = x;
		this.y = y;
	}
}

/**
 * Utility class for generating random velocities
 */
class VelocityGenerator {
	static generateVelocity() {
		let velocityX = Math.random() - 0.5;
		let velocityY = Math.random() - 0.7;

		const magnitude = Math.sqrt(
			velocityX * velocityX + velocityY * velocityY
		);

		velocityX /= magnitude;
		velocityY /= magnitude;

		return new Vector2(
			velocityX * (Math.random() * ConfettiManager.CONFIG.explosionPower),
			velocityY * (Math.random() * ConfettiManager.CONFIG.explosionPower)
		);
	}
}

/**
 * Renderer class for drawing confetti particles
 */
class Renderer {
	static clearScreen() {
		if (ConfettiManager.CTX) {
			ConfettiManager.CTX.clearRect(
				0,
				0,
				2 * window.innerWidth,
				2 * window.innerHeight
			);
		}
	}

	static drawRectangle(position, size, rotation, hue, opacity) {
		if (!ConfettiManager.CTX) return;

		ConfettiManager.CTX.save();
		ConfettiManager.CTX.beginPath();

		// Translate to center of rectangle
		ConfettiManager.CTX.translate(
			position.x + size.x / 2,
			position.y + size.y / 2
		);

		// Apply rotation
		ConfettiManager.CTX.rotate((rotation * Math.PI) / 180);

		// Draw rectangle centered at origin
		ConfettiManager.CTX.rect(-size.x / 2, -size.y / 2, size.x, size.y);

		// Set fill style with HSL color
		ConfettiManager.CTX.fillStyle = `hsla(${hue}deg, 90%, 65%, ${opacity}%)`;
		ConfettiManager.CTX.fill();
		ConfettiManager.CTX.restore();
	}
}

/**
 * Individual confetti particle
 */
class Particle {
	constructor(position) {
		// Random size based on configuration
		this.size = new Vector2(
			(16 * Math.random() + 4) * ConfettiManager.CONFIG.particleSize,
			(4 * Math.random() + 4) * ConfettiManager.CONFIG.particleSize
		);

		// Position centered on click point
		this.position = new Vector2(
			position.x - this.size.x / 2,
			position.y - this.size.y / 2
		);

		this.velocity = VelocityGenerator.generateVelocity();
		this.rotation = 360 * Math.random();
		this.rotationSpeed = 10 * (Math.random() - 0.5);
		this.hue = 360 * Math.random();
		this.opacity = 100;
		this.lifetime = Math.random() + 0.25;
	}

	update(deltaTime) {
		// Apply gravity
		this.velocity.y +=
			ConfettiManager.CONFIG.gravity *
			(this.size.y / (10 * ConfettiManager.CONFIG.particleSize)) *
			deltaTime;

		// Add random horizontal movement
		this.velocity.x += 25 * (Math.random() - 0.5) * deltaTime;

		// Apply air resistance
		this.velocity.y *= 0.98;
		this.velocity.x *= 0.98;

		// Update position
		this.position.x += this.velocity.x;
		this.position.y += this.velocity.y;

		// Update rotation
		this.rotation += this.rotationSpeed;

		// Apply fade effect if enabled
		if (ConfettiManager.CONFIG.fade) {
			this.opacity -= this.lifetime;
		}
	}

	checkBounds() {
		// Remove particle if it's below screen
		return this.position.y - 2 * this.size.x > 2 * window.innerHeight;
	}

	draw() {
		Renderer.drawRectangle(
			this.position,
			this.size,
			this.rotation,
			this.hue,
			this.opacity
		);
	}
}

/**
 * Collection of particles from a single explosion
 */
class Burst {
	constructor(position) {
		this.particles = [];

		// Create particles based on configuration
		for (let i = 0; i < ConfettiManager.CONFIG.particleCount; i++) {
			this.particles.push(new Particle(position));
		}
	}

	update(deltaTime) {
		// Update particles in reverse order for safe removal
		for (let i = this.particles.length - 1; i >= 0; i--) {
			this.particles[i].update(deltaTime);

			// Remove particles that are out of bounds
			if (this.particles[i].checkBounds()) {
				this.particles.splice(i, 1);
			}
		}
	}

	draw() {
		for (let i = this.particles.length - 1; i >= 0; i--) {
			this.particles[i].draw();
		}
	}
}

/**
 * Main confetti manager class
 */
class ConfettiManager {
	constructor(element, container) {
		if (!element) {
			throw new Error('Missing element ID');
		}

		// Initialize configuration if not exists
		if (!ConfettiManager.CONFIG) ConfettiManager.CONFIG = new ConfettiConfig();

		this.bursts = [];
		this.time = new Date().getTime();
		this.deltaTime = 0;
		this.element = null;

		this.setupCanvasContext(container);
		this.setupElement(element);

		// Start animation loop
		window.requestAnimationFrame(this.update.bind(this));
	}

	// Configuration methods
	setCount(count) {
		if (typeof count !== 'number') {
			throw new Error("Input must be of type 'number'");
		}
		ConfettiManager.CONFIG.particleCount = count;
	}

	setPower(power) {
		if (typeof power !== 'number') {
			throw new Error("Input must be of type 'number'");
		}
		ConfettiManager.CONFIG.explosionPower = power;
	}

	setSize(size) {
		if (typeof size !== 'number') {
			throw new Error("Input must be of type 'number'");
		}
		ConfettiManager.CONFIG.particleSize = size;
	}

	setFade(fade) {
		if (typeof fade !== 'boolean') {
			throw new Error("Input must be of type 'boolean'");
		}
		ConfettiManager.CONFIG.fade = fade;
	}

	setOnClick(onClick) {
		if (typeof onClick !== 'boolean') {
			throw new Error("Input must be of type 'boolean'");
		}
		ConfettiManager.CONFIG.onClick = onClick;
	}

	destroyTarget(destroy) {
		if (typeof destroy !== 'boolean') {
			throw new Error("Input must be of type 'boolean'");
		}
		ConfettiManager.CONFIG.destroyTarget = destroy;
	}

	setupCanvasContext(container) {
		if (ConfettiManager.CTX) return;

		const canvas = document.createElement('canvas');
		ConfettiManager.CTX = canvas.getContext('2d');

		// Set canvas size to double screen size for retina displays
		canvas.width = 2 * window.innerWidth;
		canvas.height = 2 * window.innerHeight;

		// Style the canvas
		canvas.style.position = 'fixed';
		canvas.style.top = '0';
		canvas.style.left = '0';
		canvas.style.width = 'calc(100%)';
		canvas.style.height = 'calc(100%)';
		canvas.style.margin = '0';
		canvas.style.padding = '0';
		canvas.style.zIndex = '999999999';
		canvas.style.pointerEvents = 'none';


		const useContainerBounds = () => {
			const bounds = container.getBoundingClientRect();
			canvas.style.top = `${bounds.top}px`;
			canvas.style.left = `${bounds.left}px`;
			canvas.style.width = 'auto';
			canvas.style.height = 'auto';
			canvas.width = `${bounds.width}`;
			canvas.height = `${bounds.height}`;
		};
		
		if (container && container instanceof HTMLElement) {
			ConfettiManager.CONTAINER = container;
			useContainerBounds();
		}

		document.body.appendChild(canvas);

		// Handle window resize
		window.addEventListener('resize', () => {
			if (ConfettiManager.CONTAINER) useContainerBounds();
			else {
				canvas.width = 2 * window.innerWidth;
				canvas.height = 2 * window.innerHeight;
			}
		});
	}

	setupElement(element) {
		this.element = typeof element === 'string'
			? document.getElementById(element)
			: element instanceof HTMLElement
			? element
			: false;

		if (!this.element) {
			throw new Error(`Element with ID '${element}' not found`);
		}

		if (!ConfettiManager.CONFIG.onClick) return;

		this.element.addEventListener('click', (event) => {
			// Play confetti at click position (doubled for retina)
			this.play(
				event.clientX * 2,
				event.clientY * 2
			);
		});
	}

	playInElement(element) {
		const x = element.offsetWidth / 2;
		const y = element.offsetHeight / 2;
		this.play(x, y);
	}

	play(x, y) {
		if (!x && !y) {
			const elementBounds = this.element.getBoundingClientRect();

			if (ConfettiManager.CONTAINER) {
				if (ConfettiManager.CONTAINER === this.element) {
					x = this.element.offsetWidth / 2;
					y = this.element.offsetHeight / 2;
				} else {
					const containerBounds = ConfettiManager.CONTAINER.getBoundingClientRect();
					x = elementBounds.left - containerBounds.left + (this.element.offsetWidth / 2);
					y = elementBounds.top - containerBounds.top  + (this.element.offsetHeight / 2);
				}
			} else {
				x = elementBounds.left + (this.element.offsetWidth / 2);
				y = elementBounds.top + (this.element.offsetHeight / 2);
			}
		}

		const position = new Vector2(x, y);
		this.bursts.push(new Burst(position));

		// Hide element if configured to do so
		if (ConfettiManager.CONFIG.destroyTarget)
			this.element.style.visibility = 'hidden';
	}

	update(timestamp) {
		// Calculate delta time
		this.deltaTime = (timestamp - this.time) / 1000;
		this.time = timestamp;

		// Update bursts in reverse order for safe removal
		for (let i = this.bursts.length - 1; i >= 0; i--) {
			this.bursts[i].update(this.deltaTime);

			// Remove empty bursts
			if (this.bursts[i].particles.length === 0) {
				this.bursts.splice(i, 1);
			}
		}

		this.draw();
		window.requestAnimationFrame(this.update.bind(this));
	}

	draw() {
		Renderer.clearScreen();

		for (const burst of this.bursts) {
			burst.draw();
		}
	}
}

// Export the ConfettiManager class
var Confetti = ConfettiManager;
